package pl.decks.personal.pages.ievp.sections.insights

import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.vars.animation.TransitionDurationVars
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.AnimationFillMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.minus
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.plus
import org.jetbrains.compose.web.css.times
import pl.decks.personal.components.animations.AppearAnimation
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout


object InsightsSectionStylesheet {
    object Vars {
        val iconSize by StyleVariable(FontTypography.Size.XXXLarge)
        val padding by StyleVariable(SiteLayout.Space.Large)
        val unfoldedMaxHeight by StyleVariable<CSSLengthNumericValue>()
        val animationStepDuration by StyleVariable(TransitionDurationVars.UltraSlow.value())
        val sectionHighlightColor by StyleVariable<Color>()
        val insightMaxWidth by StyleVariable<CSSLengthOrPercentageNumericValue>(100.percent)
    }

    object Entry {
        val UnfoldedContainer = CssStyle.base {
            Modifier.fillMaxWidth().minHeight(Vars.unfoldedMaxHeight.value())
        }
        val Folded = CssStyle {
            base {
                Modifier
                    .gap(Vars.padding.value())
                    .padding(Vars.padding.value())
                    .maxHeight(Vars.iconSize.value() + 2 * Vars.padding.value())
                    .maxWidth(Vars.iconSize.value() + 3 * Vars.padding.value())
                    .overflow(Overflow.Hidden)
                    .borderRadius(SiteLayout.Rounding.Medium)
                    .border(
                        width = SiteLayout.Line.Normal,
                        style = LineStyle.Solid,
                        color = Colors.Transparent,
                    )
                    .display(DisplayStyle.Flex)
                    .flexDirection(FlexDirection.Column)
                    .fontSize(FontTypography.Size.Small)
            }
            Breakpoint.MD {
                Modifier
                    .setVariable(Vars.insightMaxWidth, 100.percent - (Vars.iconSize.value() + 2 * Vars.padding.value()))
                    .flexDirection(FlexDirection.Row)
                    .fontSize(FontTypography.Size.Normal)
            }
        }
        val Icon = CssStyle.base {
            Modifier
                .color(Vars.sectionHighlightColor.value(colorMode.toPalette().color))
                .fontSize(Vars.iconSize.value())
                .transition {
                    property("opacity")
                    duration(Vars.animationStepDuration.value())
                    timingFunction(TransitionTimingFunction.EaseInOut)
                }
        }
        val Content = CssStyle.base {
            Modifier
                .maxWidth(Vars.insightMaxWidth.value())
                .animation(
                    AppearAnimation.Frames.toAnimation(
                        duration = Vars.animationStepDuration.value(),
                        timingFunction = AnimationTimingFunction.EaseOut,
                        fillMode = AnimationFillMode.Forwards,
                    )
                )
        }
        val InsightText = FontTypography.serif
        val InsightTextEmphasis = InsightText.extendedByBase {
            Modifier
                .fontWeight(FontTypography.Weight.Medium)
                .color(Vars.sectionHighlightColor.value(colorMode.toPalette().color))
        }
    }

    val UnfoldFrames = Keyframes {
        from {
            Modifier
                .maxHeight(Vars.iconSize.value() + 2 * Vars.padding.value())
                .maxWidth(Vars.iconSize.value() + 2 * Vars.padding.value())
                .border(
                    width = SiteLayout.Line.Normal,
                    style = LineStyle.Solid,
                    color = Colors.Transparent,
                )
        }
        50.percent {
            Modifier
                .maxHeight(Vars.iconSize.value() + 2 * Vars.padding.value())
                .maxWidth(100.percent)
                .fillMaxWidth()
                .border(
                    width = SiteLayout.Line.Normal,
                    style = LineStyle.Solid,
                    color = colorMode.toPalette().border,
                )
        }
        to {
            Modifier
                .maxHeight(Vars.unfoldedMaxHeight.value() - Vars.padding.value())
                .maxWidth(100.percent)
                .fillMaxWidth()
                .fillMaxHeight()
                .border(
                    width = SiteLayout.Line.Normal,
                    style = LineStyle.Solid,
                    color = Vars.sectionHighlightColor.value(colorMode.toPalette().border),
                )
        }
    }

    val Section = CssStyle.base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = Vars.unfoldedMaxHeight.value() * 0.5)
            .setVariable(Vars.sectionHighlightColor, colorMode.toSitePalette().accents.primary)
    }
}
